import type { WeatherReading } from './types.js'

const ENDPOINT = 'https://api.open-meteo.com/v1/forecast'
const CACHE_PREFIX = 'kil:v2:weather:'

/** Innerhalb dieser Zeit wird gar nicht erst nachgefragt. */
export const TTL_MS = 10 * 60 * 1000
/** Bis hierher dient ein alter Eintrag noch als Notfall-Fallback, wenn der Abruf scheitert. */
export const STALE_MAX_MS = 3 * 60 * 60 * 1000
/**
 * Ab dieser Ortsunschärfe ist der abgerufene Punkt womöglich eine ganz andere Wetterlage.
 * Auf dem Desktop liefert die Standortbestimmung oft nur eine WLAN- oder IP-Schätzung.
 */
export const ACCURACY_WARN_M = 5000

export class WeatherError extends Error {}

export interface Located {
  latitude: number
  longitude: number
  /** Radius der Ortsunschärfe in Metern, wie ihn die Geolocation-API meldet. */
  accuracyM: number
}

interface CacheEntry {
  t: number
  rh: number
  fetchedAt: number
  observedAt: number
}

/** Auf ~1 km runden: meteorologisch ausreichend, bessere Cache-Trefferquote, weniger Standortpreisgabe. */
function cacheKey(lat: number, lon: number): string {
  return `${CACHE_PREFIX}${lat.toFixed(2)}:${lon.toFixed(2)}`
}

function readCache(key: string): CacheEntry | null {
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return null
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null) return null
    const entry = parsed as Record<string, unknown>
    if (
      typeof entry['t'] !== 'number' ||
      typeof entry['rh'] !== 'number' ||
      typeof entry['fetchedAt'] !== 'number' ||
      typeof entry['observedAt'] !== 'number'
    ) {
      return null
    }
    return {
      t: entry['t'],
      rh: entry['rh'],
      fetchedAt: entry['fetchedAt'],
      observedAt: entry['observedAt'],
    }
  } catch {
    return null
  }
}

function writeCache(key: string, entry: CacheEntry): void {
  try {
    localStorage.setItem(key, JSON.stringify(entry))
  } catch {
    // Cache ist optional.
  }
}

/**
 * Standort des Nutzers. Löst nur nach expliziter Freigabe auf.
 * `enableHighAccuracy` fragt nach GPS statt nach der groben WLAN-/IP-Schätzung; auf dem
 * Desktop bleibt es trotzdem oft bei mehreren Kilometern Unschärfe – deshalb wird
 * `accuracyM` mit zurückgegeben und in der Oberfläche angezeigt.
 */
export function currentPosition(): Promise<Located> {
  return new Promise((resolve, reject) => {
    if (!('geolocation' in navigator)) {
      reject(new WeatherError('Dieser Browser kennt keine Standortbestimmung.'))
      return
    }
    navigator.geolocation.getCurrentPosition(
      (pos) =>
        resolve({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          accuracyM: pos.coords.accuracy,
        }),
      (err) =>
        reject(
          new WeatherError(
            err.code === err.PERMISSION_DENIED
              ? 'Standortfreigabe abgelehnt. Außenwerte bitte von Hand eintragen.'
              : 'Standort konnte nicht bestimmt werden.',
          ),
        ),
      { enableHighAccuracy: true, timeout: 15_000, maximumAge: 2 * 60 * 1000 },
    )
  })
}

/**
 * Der Zeitstempel des Messwerts, nicht der des Abrufs.
 * Ohne `timezone`-Parameter antwortet Open-Meteo in UTC, `2026-08-30T10:15` meint also 10:15 UTC.
 */
function parseObservedAt(time: unknown, fallback: number): number {
  if (typeof time !== 'string') return fallback
  const parsed = Date.parse(`${time}Z`)
  return Number.isFinite(parsed) ? parsed : fallback
}

/**
 * Aktuelle Außenwerte für eine Position.
 * Reihenfolge: frischer Cache -> Netzabruf -> abgelaufener Cache als Fallback.
 */
export async function fetchOutdoor(
  lat: number,
  lon: number,
  now = Date.now(),
): Promise<WeatherReading> {
  const key = cacheKey(lat, lon)
  const cached = readCache(key)
  const roundedLat = Number(lat.toFixed(2))
  const roundedLon = Number(lon.toFixed(2))

  if (cached && now - cached.fetchedAt < TTL_MS) {
    return {
      t: cached.t,
      rh: cached.rh,
      fetchedAt: cached.fetchedAt,
      observedAt: cached.observedAt,
      latitude: roundedLat,
      longitude: roundedLon,
      stale: false,
    }
  }

  const url = `${ENDPOINT}?latitude=${roundedLat}&longitude=${roundedLon}&current=temperature_2m,relative_humidity_2m`

  try {
    const response = await fetch(url)
    if (!response.ok) throw new WeatherError(`Wetterdienst antwortete mit ${response.status}.`)
    const data: unknown = await response.json()
    const payload = data as { current?: Record<string, unknown> }
    const current = payload.current
    const t = current?.['temperature_2m']
    const rh = current?.['relative_humidity_2m']
    if (typeof t !== 'number' || typeof rh !== 'number') {
      throw new WeatherError('Antwort des Wetterdienstes enthielt keine Messwerte.')
    }
    const observedAt = parseObservedAt(current?.['time'], now)
    writeCache(key, { t, rh, fetchedAt: now, observedAt })
    return {
      t,
      rh,
      fetchedAt: now,
      observedAt,
      latitude: roundedLat,
      longitude: roundedLon,
      stale: false,
    }
  } catch (error) {
    if (cached && now - cached.fetchedAt < STALE_MAX_MS) {
      return {
        t: cached.t,
        rh: cached.rh,
        fetchedAt: cached.fetchedAt,
        observedAt: cached.observedAt,
        latitude: roundedLat,
        longitude: roundedLon,
        stale: true,
      }
    }
    throw error instanceof WeatherError
      ? error
      : new WeatherError('Wetterdaten konnten nicht geladen werden.')
  }
}

/** "vor 4 Min." – kurze, relative Altersangabe. */
export function formatAge(timestamp: number, now = Date.now()): string {
  const minutes = Math.floor((now - timestamp) / 60_000)
  if (minutes < 1) return 'gerade eben'
  if (minutes === 1) return 'vor 1 Minute'
  if (minutes < 60) return `vor ${minutes} Minuten`
  const hours = Math.floor(minutes / 60)
  return hours === 1 ? 'vor 1 Stunde' : `vor ${hours} Stunden`
}

/** "±120 m" bzw. "±25 km" – die Ortsunschärfe in lesbarer Form. */
export function formatAccuracy(accuracyM: number): string {
  if (!Number.isFinite(accuracyM)) return ''
  return accuracyM >= 1000
    ? `±${Math.round(accuracyM / 1000)} km`
    : `±${Math.round(accuracyM)} m`
}
